/**
 * Panel de **estadísticas del cotizador** (paridad con la pestaña Estadísticas del SSR, admin).
 *
 * Superconjunto de lo que mostraba el SSR (`templates/cotizador_puertas.html`,
 * `loadDashboardEstadisticas`) más un extra del React. De arriba a abajo:
 *  1. **Error vs vendedor** (dato de servidor, EXTRA que el SSR no tenía):
 *     `GET /api/v1/cotizador/cotizaciones/stats` vía `useCotizacionStats`. Resume el error
 *     absoluto (%) entre la cotización y el total manual del vendedor (conteo + media/mediana/p95).
 *  2. **3 KPIs** (`StatsKpiRow`): ganancia neta, costo material, IVA.
 *  3. **Distribución del dinero cobrado** (`StatsDistribution`): torta de 4 categorías +
 *     leyenda + bloque "Tu ganancia real" con los 4 pasos.
 *  4. **Detalle por componente** (`StatsComponentTable`): de dónde viene cada peso + footer resumen.
 *
 * Todos los datos de #2–#4 ya vienen en `CalcResult` + `stats_desktop` que devuelve
 * `/calcular`; aquí solo se formatea/agrega (sin tocar el motor de cálculo). Sin un cálculo
 * reciente se muestra el estado vacío.
 */
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui";

import { cotizadorErrorMessage, formatPercent } from "../format";
import { useCotizacionStats } from "../hooks/useCotizador";
import type { CalcResult } from "../types";
import { StatsComponentTable } from "./StatsComponentTable";
import { StatsDistribution } from "./StatsDistribution";
import { StatsKpiRow } from "./StatsKpiRow";

interface StatsPanelProps {
  /** Última cotización calculada (alimenta KPIs, distribución y tabla), o `null`. */
  lastCalc: CalcResult | null;
}

export function StatsPanel({ lastCalc }: StatsPanelProps) {
  // `mine=false`: el admin ve las stats de toda su sede (igual que el SSR pedía `mine=0`).
  const { data, isLoading, isError, error } = useCotizacionStats(true, 30, false);
  const hasCalc = Boolean(lastCalc?.stats_desktop);

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>Error frente al vendedor (últimos 30 días)</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-slate-500" role="status">
              Cargando…
            </p>
          ) : isError ? (
            <p className="text-sm text-red-600" role="alert">
              {cotizadorErrorMessage(error)}
            </p>
          ) : !data || data.count === 0 ? (
            <p className="text-sm text-slate-500">
              Aún no hay cotizaciones con comparación manual para medir el error.
            </p>
          ) : (
            <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <div>
                <dt className="text-xs uppercase tracking-wide text-slate-500">Registros</dt>
                <dd className="text-xl font-bold text-slate-900">{data.count}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-slate-500">Error medio</dt>
                <dd className="text-xl font-bold text-slate-900">
                  {formatPercent(data.mean_abs_error_pct)}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-slate-500">Mediana</dt>
                <dd className="text-xl font-bold text-slate-900">
                  {formatPercent(data.median_abs_error_pct)}
                </dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-slate-500">P95</dt>
                <dd className="text-xl font-bold text-slate-900">
                  {formatPercent(data.p95_abs_error_pct)}
                </dd>
              </div>
            </dl>
          )}
        </CardContent>
      </Card>

      {hasCalc && lastCalc ? (
        <>
          <StatsKpiRow calc={lastCalc} />
          <StatsDistribution calc={lastCalc} />
          <StatsComponentTable calc={lastCalc} />
        </>
      ) : (
        <Card>
          <CardContent className="py-8">
            <p className="text-center text-sm text-slate-500">
              Realiza una cotización para ver a dónde va cada peso cobrado.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
